import { useCallback, useMemo, useState } from "react";
import { PAGE_SIZE } from "./location-mapping.constants";
import type { DropdownItem, ProcessedRow } from "./location-mapping.types";
import { allRows, normalizeText } from "./location-mapping.utils";

export type { DropdownItem, ProcessedRow };

export const useLocationMapping = () => {
  const [searchInput, setSearchInput] = useState("");

  const [page, setPage] = useState(1);

  /*
   All rows whose normalized columns include the normalized search term.

  Example — typing "cần":
    Input:  searchInput = "cần" -> term = "can"
    => The row of "Cần Đăng"  is chosen because "candang" includes "can"
    => The row of "Năm Căn" is chosen because "namcan" includes "can"
    Output: filteredRows = [
      { original: ["An Giang", "Cần Đăng", "Xã Vĩnh Lợi, Xã Cần Đăng"], normalized: ["angiang", "candang", "xavinhloicandang"] },
      { original: ["Cà Mau", "Năm Căn", "Xã Hàng Vinh, Thị trấn Năm Căn"], normalized: ["camau", "namcan", "xahangvinhnamcan"] },
      ...,
    ];
  */
  const filteredRows = useMemo((): ProcessedRow[] => {
    const term = normalizeText(searchInput.trim());
    if (!term) return allRows;

    return allRows.filter(
      (row) =>
        row.normalized[0].includes(term) ||
        row.normalized[1].includes(term) ||
        row.normalized[2].includes(term),
    );
  }, [searchInput]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE)),
    [filteredRows],
  );

  /*
    Page clamp strategy:
      if user is on page 7 of 10 pages, types sth and get 3 pages of results, we temporarily set currentPage to 3 until the next render.
      If user clears search, totalPages grows back 10 and we restore currentPage to 7.
  */
  const currentPage = useMemo(() => Math.min(page, totalPages), [page, totalPages]);

  const paginatedRows = useMemo((): ProcessedRow[] => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredRows.slice(start, start + PAGE_SIZE);
  }, [filteredRows, currentPage]);

  const goToPage = useCallback(
    (n: number) => {
      setPage(Math.min(Math.max(1, n), totalPages));
    },
    [totalPages],
  );

  /*
     Autocomplete dropdown options:
     From all rows that match the search term,
     we get exactly the cell values that contain the search term to show
  */
  const dropdownItems = useMemo((): DropdownItem[] => {
    const term = normalizeText(searchInput.trim());
    // show suggestions only when length > 2
    if (searchInput.trim().length <= 2 || !term) return [];

    /*
       a) Unique cell values from every column of every matching row.
          Example:
          filteredRows :
            row1: ["An Giang",  "Cần Đăng", "Xã Vĩnh Lợi, Xã Cần Đăng"]
          =>
          uniqueCells = {
            "angiang"         : "An Giang",
            "candang"         : "Cần Đăng",
            "xavinhloicandang": "Xã Vĩnh Lợi, Xã Cần Đăng",
            ...
          }
    */
    const uniqueCells = new Map<string, string>(); // normalized → display
    for (const row of filteredRows) {
      for (let col = 0; col < 3; col++) {
        const normalizedCell = row.normalized[col];
        if (!uniqueCells.has(normalizedCell))
          uniqueCells.set(normalizedCell, row.original.columns[col]);
      }
      if (uniqueCells.size >= 500) break;
    }

    /*
      (b) Keep only cells whose text itself contains the search term
        Example — term = "can":
          "angiang" ⊄ "can"  ✗  drop
          "candang" ⊃ "can"  ✓  keep
          "camau"   ⊄ "can"  ✗  drop
          "namcan"  ⊃ "can"  ✓  keep
        matched = [
          { normalized: "candang", display: "Cần Đăng" },
          { normalized: "namcan",  display: "Năm Căn" },
          ...
        ]
    */
    const matched: DropdownItem[] = [];
    for (const [normalized, display] of uniqueCells) {
      if (normalized.includes(term)) matched.push({ normalized, display });
    }

    /*
      c) Rank: starts-with term first, then Vietnamese alphabetical.
      Example — term = "can":
        "candang" starts with "can"  → rank 0  (first tier)
        "namcan"  does not           → rank 1  (second tier)
      Final order: [
        { normalized: "candang", display: "Cần Đăng" },
        { normalized: "namcan",  display: "Năm Căn" },
        ...
      ]
    */
    matched.sort((a, b) => {
      const rank =
        (a.normalized.startsWith(term) ? 0 : 1) - (b.normalized.startsWith(term) ? 0 : 1);
      return rank !== 0 ? rank : a.display.localeCompare(b.display, "vi");
    });

    return matched.slice(0, 500);
  }, [filteredRows, searchInput]);

  return {
    // Search
    searchInput,
    setSearchInput,

    // Table
    filteredRows,
    paginatedRows,

    // Pagination
    currentPage,
    totalPages,
    goToPage,

    // Autocomplete options
    dropdownItems,
  };
};
